import React, { useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Alert, Button, Card, DatePicker, Space, Spin, Typography, message } from "antd";
import { PlusOutlined, ReloadOutlined, SaveOutlined } from "@ant-design/icons";
import axios from "axios";
import moment from "moment";
import jspreadsheet from "jspreadsheet-ce";
import { AppContext } from "../context/AppContext";
import {
  JUTEM_COMPANY_ID,
  fetchBorrowerRegistry,
  matchBorrower,
  saveLedgerMonth,
} from "../utils/jutemLedgerService";

const { Title, Text } = Typography;
const DIRECTUS_URL = "https://zedlenders.pickmesms.com";

// Matches jutemEtlUploadPage.jsx's Loans worksheet exactly, so the two pages
// present the same shape as the source Excel sheet.
const COLUMNS = [
  "NAME",
  "AMOUNT LOANED",
  "INTEREST",
  "EXPECTED AMOUNT",
  "DATE EXPECTED",
  "PROFIT",
  "STATUS",
  "COMMISSION",
];

const EMPTY_ROWS = 8;
const blankRows = (n) =>
  Array.from({ length: n }, () => Array.from({ length: COLUMNS.length }, () => ""));

const toDayOrdinal = (isoDate) => {
  if (!isoDate) return "";
  const d = moment(isoDate);
  if (!d.isValid()) return "";
  const day = d.date();
  const suffix =
    day % 10 === 1 && day !== 11
      ? "st"
      : day % 10 === 2 && day !== 12
        ? "nd"
        : day % 10 === 3 && day !== 13
          ? "rd"
          : "th";
  return `${day}${suffix}`;
};

const JutemLedgerPage = () => {
  const { company } = useContext(AppContext);
  const navigate = useNavigate();

  const [month, setMonth] = useState(moment());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);
  const containerRef = useRef(null);
  const gridRef = useRef(null);
  const registryRef = useRef([]);

  useEffect(() => {
    if (company && company.id !== JUTEM_COMPANY_ID) {
      message.warning("This page is only for Jutem Fund (Company ID 22)");
      navigate("/");
    }
  }, [company, navigate]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    let cancelled = false;

    (async () => {
      try {
        registryRef.current = await fetchBorrowerRegistry();
      } catch {
        // Profile links just won't resolve if this fails — not fatal.
        registryRef.current = [];
      }
      if (cancelled) return;

      try {
        jspreadsheet.destroy(container);
      } catch {
        /* nothing to destroy */
      }
      container.innerHTML = "";

      try {
        const instance = jspreadsheet(container, {
          worksheets: [
            {
              worksheetName: "Loans",
              data: blankRows(EMPTY_ROWS),
              columns: [
                {
                  type: "text",
                  title: "NAME",
                  width: 200,
                  // Keeps the cell editable like every other cell, but adds a
                  // small link inside it that navigates to the matched
                  // borrower's profile without entering edit mode.
                  render: (cell, value) => {
                    cell.textContent = "";
                    cell.appendChild(document.createTextNode(value || ""));

                    const borrower = value ? matchBorrower(registryRef.current, value) : null;
                    if (!borrower) return;

                    const link = document.createElement("a");
                    link.textContent = " ↗";
                    link.title = "View borrower profile";
                    link.href = `/borrower/${borrower.id}`;
                    link.style.marginLeft = "4px";
                    link.style.textDecoration = "none";
                    link.addEventListener("click", (e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      navigate(`/borrower/${borrower.id}`);
                    });
                    cell.appendChild(link);
                  },
                },
                { type: "numeric", title: "AMOUNT LOANED", width: 130 },
                { type: "numeric", title: "INTEREST", width: 100 },
                { type: "numeric", title: "EXPECTED AMOUNT", width: 140 },
                { type: "text", title: "DATE EXPECTED", width: 120 },
                { type: "numeric", title: "PROFIT", width: 120 },
                { type: "dropdown", title: "STATUS", width: 110, source: ["Paid", "Unpaid"] },
                { type: "numeric", title: "COMMISSION", width: 120 },
              ],
              minDimensions: [COLUMNS.length, EMPTY_ROWS],
              allowInsertRow: true,
              allowManualInsertRow: true,
              allowInsertColumn: false,
              allowDeleteColumn: false,
            },
          ],
        });
        gridRef.current = Array.isArray(instance) ? instance[0] : instance;
        loadMonth(month);
      } catch (err) {
        gridRef.current = null;
        message.error(`Couldn't start the grid editor: ${err.message}`);
      }
    })();

    return () => {
      cancelled = true;
      try {
        jspreadsheet.destroy(container);
      } catch {
        /* already destroyed */
      }
      gridRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadMonth = async (m) => {
    const grid = gridRef.current;
    if (!grid) return;
    setLoading(true);
    setResult(null);
    try {
      const monthKey = m.format("YYYY-MM");
      const res = await axios.get(`${DIRECTUS_URL}/items/amortization`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        params: {
          "filter[loan_reference][company][_eq]": JUTEM_COMPANY_ID,
          fields:
            "*,loan_reference.amount,loan_reference.borrower.first_name,loan_reference.borrower.last_name",
          limit: -1,
          sort: "-due_date",
        },
      });
      const rows = (res.data.data || [])
        .filter((r) => (r.due_date || "").startsWith(monthKey))
        .map((r) => {
          const b = r.loan_reference?.borrower;
          const name = b ? `${b.first_name || ""} ${b.last_name || ""}`.trim() : "";
          const statusLower = (r.status || "").toLowerCase();
          return [
            name,
            r.loan_reference?.amount ?? "",
            r.interest_rate ?? "",
            r.expected_amount ?? "",
            toDayOrdinal(r.due_date),
            r.profit ?? "",
            statusLower === "paid" ? "Paid" : "Unpaid",
            "",
          ];
        });
      grid.setData(rows.length ? rows : blankRows(EMPTY_ROWS));
      if (rows.length) {
        message.success(`Loaded ${rows.length} entr${rows.length === 1 ? "y" : "ies"} for ${m.format("MMMM YYYY")}`);
      } else {
        message.info(`No existing entries for ${m.format("MMMM YYYY")} — starting blank`);
      }
    } catch (err) {
      message.error("Couldn't load this month's ledger");
    } finally {
      setLoading(false);
    }
  };

  const handleMonthChange = (m) => {
    if (!m) return;
    setMonth(m);
    loadMonth(m);
  };

  const handleAddRow = () => {
    gridRef.current?.insertRow?.();
  };

  const handleSave = async () => {
    const grid = gridRef.current;
    if (!grid) return;
    const rows = grid.getData();
    setSaving(true);
    setResult(null);
    try {
      const summary = await saveLedgerMonth(rows, month);
      setResult(summary);
      if (summary.errors.length) {
        message.warning(`Saved with ${summary.errors.length} issue(s) — see below`);
      } else {
        message.success(`Saved ${summary.saved} entr${summary.saved === 1 ? "y" : "ies"}`);
      }
      if (summary.newBorrowers > 0) {
        try {
          registryRef.current = await fetchBorrowerRegistry();
        } catch {
          /* profile links for the new borrowers just won't resolve yet */
        }
      }
      await loadMonth(month);
    } catch (err) {
      message.error(`Save failed: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ padding: 24 }}>
      <Title level={3}>Jutem Fund — Ledger</Title>
      <Text type="secondary" style={{ display: "block", marginBottom: 16 }}>
        Edit directly like a spreadsheet. Save writes straight to the system —
        no file upload, no waiting on a review step.
      </Text>

      <Card>
        <Space wrap style={{ marginBottom: 16 }}>
          <Text strong>Month:</Text>
          <DatePicker picker="month" value={month} onChange={handleMonthChange} allowClear={false} />
          <Button icon={<PlusOutlined />} onClick={handleAddRow}>
            Add row
          </Button>
          <Button icon={<ReloadOutlined />} onClick={() => loadMonth(month)}>
            Reload from system
          </Button>
        </Space>

        <Spin spinning={loading}>
          <div ref={containerRef} style={{ overflowX: "auto", marginBottom: 16 }} />
        </Spin>

        <div style={{ textAlign: "right" }}>
          <Button type="primary" size="large" icon={<SaveOutlined />} loading={saving} onClick={handleSave}>
            Save
          </Button>
        </div>

        {result && (
          <Alert
            style={{ marginTop: 16 }}
            type={result.errors.length ? "warning" : "success"}
            showIcon
            message={`Saved ${result.saved} entr${result.saved === 1 ? "y" : "ies"} — ${result.newBorrowers} new borrower${result.newBorrowers === 1 ? "" : "s"}, ${result.paymentsRecorded} payment${result.paymentsRecorded === 1 ? "" : "s"} recorded`}
            description={
              result.errors.length ? (
                <ul style={{ marginBottom: 0 }}>
                  {result.errors.map((e, i) => (
                    <li key={i}>{e}</li>
                  ))}
                </ul>
              ) : undefined
            }
          />
        )}
      </Card>
    </div>
  );
};

export default JutemLedgerPage;
